import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  MapPin, 
  Shield, 
  ChevronRight, 
  ChevronLeft,
  Loader,
  AlertCircle,
  Camera,
  Upload,
  Info
} from 'lucide-react';
import axios from 'axios';
import './KYC.css';

const COUNTRIES = {
  USA: {
    name: 'United States',
    fields: ['street', 'city', 'state', 'zipCode'],
    states: [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 
      'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 
      'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 
      'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 
      'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 
      'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 
      'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 
      'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
      'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 
      'West Virginia', 'Wisconsin', 'Wyoming'
    ],
    zipLabel: 'ZIP Code',
    stateLabel: 'State'
  },
  UK: {
    name: 'United Kingdom',
    fields: ['street', 'city', 'state', 'zipCode'],
    states: [
      'Greater London', 'Greater Manchester', 'West Midlands', 'West Yorkshire',
      'Kent', 'Essex', 'Hampshire', 'Lancashire', 'Surrey', 'Hertfordshire',
      'Merseyside', 'South Yorkshire', 'Tyne and Wear', 'Berkshire', 'Buckinghamshire',
      'Cheshire', 'Cornwall', 'Cumbria', 'Derbyshire', 'Devon'
    ],
    zipLabel: 'Postal Code',
    stateLabel: 'County'
  },
  GERMANY: {
    name: 'Germany',
    fields: ['street', 'city', 'state', 'zipCode'],
    states: [
      'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen',
      'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern',
      'North Rhine-Westphalia', 'Rhineland-Palatinate', 'Saarland',
      'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'
    ],
    zipLabel: 'Postleitzahl',
    stateLabel: 'Bundesland'
  },
  FRANCE: {
    name: 'France',
    fields: ['street', 'city', 'state', 'zipCode'],
    states: [
      'Île-de-France', 'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté',
      'Bretagne', 'Centre-Val de Loire', 'Corse', 'Grand Est', 
      'Hauts-de-France', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie',
      'Pays de la Loire', "Provence-Alpes-Côte d'Azur"
    ],
    zipLabel: 'Code Postal',
    stateLabel: 'Département'
  },
  CAMEROON: {
    name: 'Cameroon',
    fields: ['street', 'city', 'state'],
    states: [
      'Adamawa', 'Centre', 'East', 'Far North', 'Littoral',
      'North', 'Northwest', 'South', 'Southwest', 'West'
    ],
    stateLabel: 'Region'
  },
  CONGO: {
    name: 'Congo',
    fields: ['street', 'city', 'state'],
    states: [
      'Brazzaville', 'Pointe-Noire', 'Dolisie', 'Nkayi', 'Ouesso',
      'Impfondo', 'Sibiti', 'Madingou', 'Owando', 'Pokola'
    ],
    stateLabel: 'District'
  },
  SINGAPORE: {
    name: 'Singapore',
    fields: ['street', 'city', 'zipCode'],
    zipLabel: 'Postal Code'
  }
};

const NATIONALITIES = [
  'American', 'British', 'Cameroonian', 'Chinese', 'Congolese', 'French', 
  'German', 'Indian', 'Italian', 'Japanese', 'Nigerian', 'Russian', 
  'Singaporean', 'South African', 'Spanish'
].sort();

const KYC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    nationality: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    idType: '',
    idNumber: '',
    documents: {
      idImage: null,
      selfieImage: null
    }
  });

  useEffect(() => {
    return () => {
      if (formData.documents.idImagePreview) {
        URL.revokeObjectURL(formData.documents.idImagePreview);
      }
      if (formData.documents.selfieImagePreview) {
        URL.revokeObjectURL(formData.documents.selfieImagePreview);
      }
    };
  }, []);

  const getAddressFields = () => {
    const country = formData.address.country;
    return COUNTRIES[country] || {
      name: 'Other',
      fields: ['street', 'city', 'postalCode'],
      zipLabel: 'Postal Code'
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value.trim()
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value.trim() }));
    }
    setError(null);
  };

  
  const renderAddressFields = () => {
    const countryConfig = formData.address.country ? COUNTRIES[formData.address.country] : null;
    
    if (!countryConfig) {
      return null;
    }
  
    return (
      <>
        {/* Street Address - Common for all countries */}
        <div className="form-group full-width">
          <label>Street Address *</label>
          <input
            type="text"
            name="address.street"
            value={formData.address.street || ''}
            onChange={handleChange}
            placeholder="Enter your street address"
            required
          />
        </div>
  
        {/* City Field */}
        <div className="form-group">
          <label>City *</label>
          <input
            type="text"
            name="address.city"
            value={formData.address.city || ''}
            onChange={handleChange}
            placeholder="Enter your city"
            required
          />
        </div>
  
        {/* State/Region/Department Field */}
        {countryConfig.states && (
          <div className="form-group">
            <label>{countryConfig.stateLabel} *</label>
            <select
              name="address.state"
              value={formData.address.state || ''}
              onChange={handleChange}
              required
            >
              <option value="">{`Select ${countryConfig.stateLabel}`}</option>
              {countryConfig.states.map(state => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        )}
  
        {/* Postal/ZIP Code Field */}
        {countryConfig.fields.includes('zipCode') && (
          <div className="form-group">
            <label>{countryConfig.zipLabel} *</label>
            <input
              type="text"
              name="address.zipCode"
              value={formData.address.zipCode || ''}
              onChange={handleChange}
              placeholder={`Enter ${countryConfig.zipLabel}`}
              required
            />
          </div>
        )}
      </>
    );
  };

  // Update the handleCountryChange function
  const handleCountryChange = (e) => {
    const country = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        country,
        street: prev.address.street || '',
        city: '',
        state: '',
        zipCode: ''
      }
    }));
    
    setError(null);
  };

  const validateStep = (step) => {
    setError(null);
    switch (step) {
      case 1:
        if (!formData.firstName || !formData.lastName || !formData.dateOfBirth || 
            !formData.nationality || !formData.email || !formData.phone) {
          setError('Please fill in all required fields');
          return false;
        }
        // Email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        // Phone validation
        if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
          setError('Please enter a valid phone number');
          return false;
        }
        // Age validation
        const age = calculateAge(formData.dateOfBirth);
        if (age < 18) {
          setError('You must be at least 18 years old');
          return false;
        }
        break;

      case 2:
        if (!formData.address.country) {
          setError('Please select a country');
          return false;
        }
        const countryConfig = getAddressFields();
        for (const field of countryConfig.fields) {
          if (!formData.address[field]) {
            setError('Please fill in all address fields');
            return false;
          }
        }
        return validateAddress();

      case 3:
        if (!formData.idType || !formData.idNumber) {
          setError('Please complete all ID verification fields');
          return false;
        }
        return validateFiles();
  
      default:
        return true;
    }
    return true;
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };
  const handleSubmit = async () => {
    if (!validateStep(3)) return;
  
    setLoading(true);
    setError(null);
  
    try {
      const kycData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        nationality: formData.nationality,
        email: formData.email,
        phone: formData.phone,
        street: formData.address.street,
        city: formData.address.city,
        state: formData.address.state,
        zipCode: formData.address.zipCode,
        country: formData.address.country,
        idType: formData.idType,
        idNumber: formData.idNumber
      };
  
      const response = await axios.post(
        'https://swanpointb.onrender.com/api/kyc/submit',
        kycData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
  
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/profile', {
            state: { 
              message: 'KYC verification submitted successfully', 
              type: 'success' 
            }
          });
        }, 3000);
      }
    } catch (err) {
      console.error('KYC Submission Error:', err);
      setError(err.response?.data?.message || 'Failed to submit KYC. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files || !files[0]) return;
  
    const file = files[0];
    
    // Validate file size
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('File size must be less than 5MB');
      return;
    }
  
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG and PNG files are allowed');
      return;
    }
  
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [name]: file
      }
    }));
    setError(null);
  };

  const validateFiles = () => {
    if (!formData.documents.idImage || !formData.documents.selfieImage) {
      setError('Both ID and selfie images are required');
      return false;
    }
  
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  
    const validateFile = (file, fieldName) => {
      if (file.size > maxSize) {
        setError(`${fieldName} must be less than 5MB`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        setError(`${fieldName} must be a JPG or PNG file`);
        return false;
      }
      return true;
    };
  
    return validateFile(formData.documents.idImage, 'ID Image') &&
           validateFile(formData.documents.selfieImage, 'Selfie Image');
  };
 
  const validateAddress = () => {
    const countryConfig = formData.address.country ? COUNTRIES[formData.address.country] : null;
    
    if (!countryConfig) {
      setError('Please select a country');
      return false;
    }
  
    // Required fields for all countries
    if (!formData.address.street?.trim()) {
      setError('Street address is required');
      return false;
    }
  
    if (!formData.address.city?.trim()) {
      setError('City is required');
      return false;
    }
  
    // Validate state/region if the country has states
    if (countryConfig.states && !formData.address.state?.trim()) {
      setError(`${countryConfig.stateLabel || 'State/Region'} is required`);
      return false;
    }
  
    // Validate postal code if required
    if (countryConfig.zipLabel && !formData.address.zipCode?.trim()) {
      setError(`${countryConfig.zipLabel} is required`);
      return false;
    }
  
    setError(null);
    return true;
  };
  
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h2>Personal Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label>Nationality *</label>
                <select
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Nationality</option>
                  {NATIONALITIES.map(nationality => (
                    <option key={nationality} value={nationality}>
                      {nationality}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number (e.g., +1234567890)"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h2>Address Information</h2>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Country of Residence *</label>
                <select
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleCountryChange}
                  required
                >
                  <option value="">Select Country</option>
                  {Object.entries(COUNTRIES).map(([code, country]) => (
                    <option key={code} value={code}>{country.name}</option>
                  ))}
                </select>
              </div>

              {formData.address.country && renderAddressFields()}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h2>Identity Verification</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>ID Type *</label>
                <select
                  name="idType"
                  value={formData.idType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select ID Type</option>
                  <option value="passport">Passport</option>
                  <option value="nationalId">National ID</option>
                  <option value="drivingLicense">Driving License</option>
                </select>
              </div>

              <div className="form-group">
                <label>ID Number *</label>
                <input
                  type="text"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  placeholder="Enter your ID number"
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>ID Document Upload *</label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    name="idImage"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                    required
                  />
                  <div className="file-upload-box">
                    {formData.documents.idImage ? (
                      <div className="file-preview">
                        <img
                          src={URL.createObjectURL(formData.documents.idImage)}
                          alt="ID Preview"
                          className="upload-preview"
                        />
                        <p>{formData.documents.idImage.name}</p>
                      </div>
                    ) : (
                      <>
                        <Upload size={24} />
                        <p>Upload ID Document</p>
                        <span>JPG, PNG or PDF (max. 5MB)</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Selfie with ID *</label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    name="selfieImage"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                    required
                  />
                  <div className="file-upload-box">
                    {formData.documents.selfieImage ? (
                      <div className="file-preview">
                        <img
                          src={URL.createObjectURL(formData.documents.selfieImage)}
                          alt="Selfie Preview"
                          className="upload-preview"
                        />
                        <p>{formData.documents.selfieImage.name}</p>
                      </div>
                    ) : (
                      <>
                        <Camera size={24} />
                        <p>Upload Selfie with ID</p>
                        <span>JPG or PNG (max. 5MB)</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group full-width">
                <div className="info-box">
                  <Info size={20} />
                  <p>
                    Please ensure your selfie clearly shows both your face and your ID document.
                    The image should be well-lit and all information should be clearly visible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (success) {
    return (
      <div className="success-screen">
        <div className="success-content">
          <div className="success-icon">✓</div>
          <h2>KYC Submitted Successfully!</h2>
          <p>Your information has been submitted for review.</p>
          <p>We will notify you once the verification is complete.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kyc-container">
      <button onClick={() => navigate('/profile')} className="back-button">
        <ArrowLeft size={20} />
        Back to Profile
      </button>
      
      <div className="kyc-header">
        <h1>Identity Verification (KYC)</h1>
        <p>Complete your verification to access all platform features</p>
      </div>

      <div className="progress-steps">
        {[
          { step: 1, label: 'Personal Info', icon: User },
          { step: 2, label: 'Address', icon: MapPin },
          { step: 3, label: 'Verification', icon: Shield }
        ].map(({ step, label, icon: Icon }) => (
          <div
            key={step}
            className={`step ${
              currentStep === step
                ? 'active'
                : currentStep > step
                ? 'completed'
                : ''
            }`}
          >
            <div className="step-number">
              {currentStep > step ? '✓' : step}
            </div>
            <span className="step-label">{label}</span>
          </div>
        ))}
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="kyc-form">
        {renderStepContent()}

        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        <div className="form-actions">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={() => setCurrentStep(prev => prev - 1)}
              className="prev-button"
              disabled={loading}
            >
              <ChevronLeft size={20} />
              Previous
            </button>
          )}

          {currentStep < 3 ? (
            <button
              type="button"
              onClick={() => {
                if (validateStep(currentStep)) {
                  setCurrentStep(prev => prev + 1);
                }
              }}
              className="next-button"
              disabled={loading}
            >
              Next
              <ChevronRight size={20} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              className="submit-button"
              disabled={loading}
            >
              {loading ? (
                <div className="loading-spinner">
                  <Loader className="spinner" />
                  <span>Submitting...</span>
                </div>
              ) : (
                <>
                  Submit KYC
                  <Shield size={20} />
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default KYC;